import { request, requestBlob } from '@/lib/api'

/**
 * Телефония (тикет #122): звонок с карточки, запись разговора, настройки АТС.
 *
 * Поля ответа сравниваются нестрого (`== null`): сервер не кладёт в JSON пустые поля вовсе
 * (`non_null`), и `undefined` здесь — обычное «не заполнено».
 */

export type CallTargetType = 'contact' | 'counterparty' | 'lead' | 'deal'

export interface CallTarget {
  type: CallTargetType
  id: string
}

export interface TelephonyStatus {
  /** АТС подключена и включена. */
  configured: boolean
  /** Добавочный вызывающего; нет — его сотруднику не назначен номер. */
  extension?: string | null
  mayCall: boolean
}

export interface PlacedCall {
  callId: string
  activityId?: string | null
  /** `ordered` — АТС приняла, звонит ваш телефон; `rejected` — отказ; `unavailable` — АТС не ответила. */
  status: 'ordered' | 'rejected' | 'unavailable'
  message?: string | null
  number?: string | null
  extension?: string | null
}

/** Кадр живого канала «входящий звонок». */
export interface IncomingCall {
  callId: string
  number?: string | null
  direction?: string | null
  extension?: string | null
  at?: string | null
  /** Нет — номер не найден ни в одной карточке. */
  match?: {
    entityType: CallTargetType
    entityId: string
    title?: string | null
    counterpartyId?: string | null
    counterpartyName?: string | null
  } | null
  /** Сколько ещё карточек с тем же номером. */
  otherMatches?: number | null
}

export interface TelephonyExtension {
  employeeId: string
  employeeName?: string | null
  extension: string
}

export interface TelephonySettings {
  enabled: boolean
  endpoint?: string | null
  apiKeySet: boolean
  saltSet: boolean
  extensions?: TelephonyExtension[] | null
  lineNumber?: string | null
  usable: boolean
  problem?: string | null
  webhookPath: string
}

export interface TelephonySettingsUpdate {
  enabled: boolean
  endpoint: string | null
  /** `undefined` — оставить сохранённый, пустая строка — стереть. */
  apiKey?: string
  salt?: string
  /** Сотрудник → добавочный. */
  extensions: Record<string, string>
  lineNumber: string | null
}

export interface TelephonyEvent {
  id: string
  eventType: string
  eventKey?: string | null
  externalId?: string | null
  signatureValid: boolean
  status: 'received' | 'processed' | 'ignored' | 'rejected' | 'failed'
  detail?: string | null
  remoteAddress?: string | null
  attempts: number
  receivedAt: string
}

/** Карточки, на которых бывает кнопка «Позвонить», — пути их страниц. */
export const CARD_PATH: Record<CallTargetType, string> = {
  contact: '/contacts',
  counterparty: '/counterparties',
  lead: '/leads',
  deal: '/deals',
}

export function fetchTelephonyStatus(signal?: AbortSignal): Promise<TelephonyStatus> {
  return request<TelephonyStatus>('/telephony/status', { signal })
}

export function placeCall(target: CallTarget, idempotencyKey: string, field?: 'phone' | 'mobile'): Promise<PlacedCall> {
  return request<PlacedCall>('/telephony/calls', {
    method: 'POST',
    body: { entityType: target.type, entityId: target.id, field: field ?? null },
    idempotencyKey,
  })
}

/** Запись разговора байтами: ссылка АТС одноразовая и до браузера не доходит. */
export function fetchRecording(path: string, signal?: AbortSignal): Promise<Blob> {
  return requestBlob(path, signal)
}

export function fetchTelephonySettings(signal?: AbortSignal): Promise<TelephonySettings> {
  return request<TelephonySettings>('/admin/telephony', { signal })
}

export function updateTelephonySettings(update: TelephonySettingsUpdate): Promise<TelephonySettings> {
  return request<TelephonySettings>('/admin/telephony', { method: 'PUT', body: update })
}

export function fetchTelephonyEvents(signal?: AbortSignal): Promise<TelephonyEvent[]> {
  return request<TelephonyEvent[]>('/admin/telephony/events?limit=50', { signal })
}
